import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { AppChatService } from '../../core/services/appChatService.service';
import { AppChat } from '../../core/models/appChat.model';
import { MatDialog } from '@angular/material/dialog';
import { ChatNewDialogComponent } from '../chat-new-dialog/chat-new-dialog.component';
import { DOCUMENT } from '@angular/common';
import * as signalR from '@microsoft/signalr';
import { Subscription, Subject } from 'rxjs';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { AppEmpresasService } from '../../core/services/appEmpresasService.service';
import { ConsoleLogger } from '@microsoft/signalr/dist/esm/Utils';
import { LivesSessionHelper } from '../../core/helper/livesSessionHelper';

interface HrValidatorCompany {
  hrValidator: string;
  company: string;
}

@Component({
  selector: 'app-chat-detail',
  templateUrl: './chat-detail.component.html',
  styleUrls: ['./chat-detail.component.scss']
})
export class ChatDetailComponent implements OnInit, OnDestroy {

  // Propiedades del panel contenedor
  panelIcon = 'comments'; // Material icon
  panelTitle = 'Chat';
  panelDescription = 'Chat';
  // data: AppChat | null = null;
  newChatsList: any[] | null = [];

  chatsList: any[] | null = null;
  filteredChatsList: any[] | null = null;
  searchQuery: string = '';
  message: string = '';
  messages: any[] = [];
  groupedMessages: { date: string, messages: any[] }[] = [];
  headerInfo: any | null = null;
  isNewChat: boolean = false;
  room: string = '';
  currentSender: string = '';
  currentChatMember: string = '';
  loadingMessages: boolean = false;

  private messagesSubscription: Subscription = new Subscription();
  private messagesChanged: Subject<any[]> = new Subject<any[]>();
  private hrValidatorCompanyIds: HrValidatorCompany[] = [];

  constructor(
    private _appChatService: AppChatService,
    private _appEmpresasService: AppEmpresasService,
    protected _chatNewDialog: MatDialog,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private _livesSession: LivesSessionHelper,
  ) {
    this.messagesChanged.subscribe(() => {
      this.scrollToBottom();
    });
  }




  async ngOnInit(): Promise<void> {
    await this.getUserHrValidatorIds();
    await this.getChatData();
    this.filterChats()
  }

  ngOnDestroy(): void {
    this.unsubscribeFromMessages();
  }

  filterChats() {
    if (!this.searchQuery) {
      this.filteredChatsList = this.chatsList.filter(chat =>
        chat.lastMessage);
    } else {
      const query = this.searchQuery.toLowerCase();
      this.filteredChatsList = this.chatsList.filter(chat =>
        chat.lastMessage && (
          chat.chatMembersInfo[0].fullName.toLowerCase().includes(query) ||
          chat.company.name.toLowerCase().includes(query) ||
          chat.chatMembersInfo[0].userType.toLowerCase().includes(query)
        )
      );
    }
  }

  groupMessagesByDay(messages: any[]): { date: string, messages: any[] }[] {
    const groupedByDay = {};

    messages.forEach(message => {
      const date = message.sentAt?.split('T')[0];
      if (groupedByDay[date]) {
        groupedByDay[date].push(message);
      } else {
        groupedByDay[date] = [message];
      }
    });

    const groupedMessages = Object.keys(groupedByDay).map(date => ({
      date,
      messages: groupedByDay[date],
    }));

    groupedMessages.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return groupedMessages;
  }

  formatChatDividerDate(dateString: string) {
    const date = new Date(dateString);
    const today = new Date();

    // Obtener la fecha de ayer
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Función para formatear una fecha (día, mes y año)
    function formatDate(date) {
      var day = ("0" + date.getDate()).slice(-2);
      var monthNumbered = ("0" + (date.getMonth() + 1)).slice(-2);
      var year = date.getFullYear();
      return day + '/' + monthNumbered + '/' + year;
    }

    // Comprobar si la fecha es la misma (ignorando la hora)
    var isSameDate = date.toDateString() === today.toDateString();

    // Comprobar si la fecha es ayer
    var isYesterday = date.toDateString() === yesterday.toDateString();

    var chatDate = '';

    if (isSameDate) {
      // Si es el mismo día, usar la hora
      chatDate = 'Hoy';
    } else if (isYesterday) {
      // Si es ayer
      chatDate = 'Ayer';
    } else {
      // Si no, usar la fecha formateada
      chatDate = formatDate(date);
    }
    return chatDate;
  }


  // Método para obtener los IDs de los validadores
  async getUserHrValidatorIds(): Promise<void> {
    const sessionId = this._livesSession.AspNetUserId;
    return new Promise<void>((resolve, reject) => {
      this._appEmpresasService.getHrValidatorCompaniesIds(sessionId).subscribe({
        next: (result) => {
          this.hrValidatorCompanyIds = result.companies.map((company: any) => ({
            hrValidator: company.hrValidator1,
            company: company.company1
          }));
          resolve(); // Resuelve la promesa una vez obtenidos los IDs
        },
        error: (err) => {
          console.error('Error obteniendo los id de los validadores de rrhh de las empresas', err);
          reject(err); // Rechaza la promesa si hay un error
        }
      });
    });
  }



  async getChatData(): Promise<void> {

    this.hrValidatorCompanyIds.forEach((company) => {
      this._appChatService.getNewChats(company.hrValidator, company.company).subscribe({
        next: (result) => {
          const uniqueResults = result.filter(chat => !this.newChatsList.some(existingChat => existingChat.userId === chat.userId));

          this.newChatsList = this.newChatsList.concat(uniqueResults);
        },
        error: (err) => {
          console.error('Error al obtener los chats', err);
        }
      });
    });

    try {
      let chatRooms: any[] = [];
      const chatRoomsPromises = this.hrValidatorCompanyIds.map(async (company) => {

        const chatRoomsForUser = await this._appChatService.getChatRoomsForUser(company.hrValidator);

        return chatRoomsForUser.map(chatRoom => ({
          ...chatRoom,
          hrValidator: company.hrValidator  // Añadir hrValidator a cada chatRoom
        }));
      });

      const results = await Promise.all(chatRoomsPromises);
      chatRooms = results.reduce((acc, curr) => acc.concat(curr), []);

      this.chatsList = chatRooms
        .sort((a, b) => new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime())
        .map(chat => {
          const adjustedTime = this.adjustTimeToSystemTimezone(chat.lastMessageTime);
          return {
            ...chat,
            lastMessageTime: this.formattedCardDate(adjustedTime.iso),
            chatMembersInfo: chat.chatMembersInfo.filter(member => member.user !== chat.hrValidator)
          };
        });
      this.filterChats()
    } catch (err) {
      console.error('Error al obtener las salas', err);
    }

  }


  private async handleNewChatRoom(userSelected: any): Promise<void> {
    this.clearMessages();

    this.headerInfo = {
      chatMembersInfo: [{
        userId: userSelected.userId,
        userType: userSelected.userType,
        fullName: userSelected.fullName,
        initials: userSelected.initials,
        profileImage: userSelected.profileImage,
        company: userSelected.company,
      }],
      company: userSelected.company,
    };

    var usersFormatted = {
      userId: userSelected.userId,
      userType: userSelected.userType,
    }

    try {
      this.hrValidatorCompanyIds.forEach(async (company) => {
        if (company.company === userSelected.company.company1) {
          const { roomId, memberIds } = await this._appChatService.createChatRoom(
            company.hrValidator,
            [usersFormatted],
            userSelected.company.company1
          );
          this.room = roomId;
          this.currentSender = company.hrValidator;
          this.isNewChat = true;

          this.subscribeToMessages(this.room);
          await this.getChatData();
        }
      });
    } catch (error) {
      console.error('Error al crear la sala:', error);
    }

  }

  private async handleChatRoom(roomSelected: any): Promise<void> {
    // Desuscribirse de mensajes y limpiar mensajes
    this.unsubscribeFromMessages();
    this.clearMessages();
    this.loadingMessages = true;
    this.headerInfo = roomSelected;
    try {
      // Buscar el hrValidator correspondiente a la compañía de roomSelected
      const companyInfo = this.hrValidatorCompanyIds.find(
        company => company.company === roomSelected.company.company1
      );

      if (!companyInfo) {
        throw new Error('Company not found in hrValidatorCompanyIds');
      }

      // Unirse a la sala de chat
      this.currentChatMember = await this._appChatService.joinChatRoom(companyInfo.hrValidator, roomSelected.roomId);
      const messages = await this._appChatService.getMessagesFromRoom(roomSelected.roomId);
      const formattedMessages = this._appChatService.formatMessages(messages);
      this.room = roomSelected.roomId;
      this.currentSender = companyInfo.hrValidator;
      this.isNewChat = false;

      // Filtrar mensajes duplicados antes de actualizar
      const uniqueMessages = formattedMessages.filter(
        msg => !this.messages.some(existingMsg => existingMsg.id === msg.id)
      );
      this.updateMessages(uniqueMessages);
      this.subscribeToMessages(this.room);
      await this.getChatData(); // Asegurarse de esperar a que los datos del chat se carguen
    } catch (error) {
      console.error('Error uniéndose a la sala:', error);
    }
    this.loadingMessages = false;
  }



  private subscribeToMessages(room: string): void {
    this.unsubscribeFromMessages();
    this.messagesSubscription = this._appChatService.messages$
      .pipe(
        filter((messages: any[]) => {
          const result = messages.some(message => message.roomId === room);

          return result;
        }),
        distinctUntilChanged()
      )
      .subscribe(messages => {
        const messageIds = new Set(this.messages.map(msg => msg.id));
        const uniqueMessages = messages
          .filter(message => message.roomId === room)
          .filter(msg => !messageIds.has(msg.id));

        if (uniqueMessages.length > 0) {
          this.updateMessages(uniqueMessages);
        }
      });
  }

  updateMessages(messages: any[]): void {
    if (this.room) {
      // Crear una lista de IDs de los mensajes actuales
      const messageIds = this.messages.map(msg => msg.id);
      const uniqueMessages: any[] = [];
      // Verificar y filtrar los mensajes duplicados
      messages.forEach(msg => {
        let exists = false;
        for (const id of messageIds) {
          if (msg.id.toString() === id.toString()) {
            exists = true;
            break;
          }
        }

        if (!exists) {
          uniqueMessages.push(msg);
          messageIds.push(msg.id); // Añadir el ID del mensaje a la lista
        }
      });

      if (uniqueMessages.length > 0) {
        this.messages = [...this.messages, ...uniqueMessages];

        this.messages.sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());

        this.messagesChanged.next(this.messages);

        this.groupedMessages = this.groupMessagesByDay(this.getFilteredMessages());


        // Añadir la llamada a UpdateMessagesFromRoom después de actualizar los mensajes
        uniqueMessages.forEach(msg => {

          const currentPath = window.location.pathname;
          if (currentPath.endsWith('/app-chat') && msg.roomId === this.room) {
            // console.log(currentPath.endsWith('/app-chat'), currentPath, msg.roomId === this.room, msg.roomId, this.room)
            if (!msg.isRead && msg.sender !== this.currentChatMember) {
              this._appChatService.UpdateMessagesFromRoom(this.room, msg.user, msg.messageId);
              // console.log('readed ', msg)

            }

          }
        });
      }
    }
  }

  getFilteredMessages(): any[] {
    const filteredMessages = this.messages.filter(msg => msg.sender !== 'admin');
    const uniqueFilteredMessages = filteredMessages.filter(
      (msg, index, self) => index === self.findIndex(m => m.id === msg.id)
    );
    return uniqueFilteredMessages;
  }

  clearMessages(): void {
    this.messages = [];
    this.messagesChanged.next(this.messages);

    this.groupedMessages = [];
  }

  private unsubscribeFromMessages(): void {
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
      this.messagesSubscription = new Subscription();
    }
  }

  onClickNew(): void {
    this.message = '';
    const dialogRef = this._chatNewDialog.open(ChatNewDialogComponent, {
      width: '30%',
      data: this.newChatsList
    });

    const dialogContainer = this.document.querySelector('.mat-dialog-container');
    if (dialogContainer) {
      this.renderer.setStyle(dialogContainer, 'background-color', '#dadada');
    }

    dialogRef.afterClosed().subscribe(userSelected => {
      for (const conversation of this.chatsList) {
        for (const member of conversation.chatMembersInfo) {
          if (userSelected.userId === member.user) {
            this.handleChatRoom(conversation)
            return;
          }
        }
      }
      if (userSelected) {
        this.handleNewChatRoom(userSelected);
      }
    });
  }

  async onClickSend() {
    if (this.room && this.message.trim() !== '') {
      await this._appChatService.sendMessage(this.room, this.currentSender, this.message);
      this.message = '';
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onClickSend();
    }
  }







  scrollToBottom(animated = true): void {
    setTimeout(() => {
      try {
        const chatContainer = this.document.querySelector('.chat-messages-container');
        if (chatContainer) {
          if (animated) {
            chatContainer.scrollTo({ top: chatContainer.scrollHeight, behavior: 'smooth' });
          } else {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }
      } catch (err) {
        console.error('Error al scrolear al final:', err);
      }
    }, 100); // Temporizador de 100ms
  }



  formattedCardDate(dateString: string) {
    const date = new Date(dateString);

    // Obtener la fecha de hoy
    const today = new Date();

    // Obtener la fecha de ayer
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Función para formatear una fecha (día, mes y año)
    function formatDate(date) {
      var day = ("0" + date.getDate()).slice(-2);
      var monthNumbered = ("0" + (date.getMonth() + 1)).slice(-2);
      var year = date.getFullYear();
      return day + '/' + monthNumbered + '/' + year;
    }

    // Función para formatear la hora
    function formatTime(date) {
      var hours = ("0" + date.getHours()).slice(-2);
      var minutes = ("0" + date.getMinutes()).slice(-2);
      return hours + ':' + minutes;
    }

    // Comprobar si la fecha es la misma (ignorando la hora)
    var isSameDate = formatDate(date) === formatDate(today);

    // Comprobar si la fecha es ayer
    var isYesterday = formatDate(date) === formatDate(yesterday);

    var chatDate = '';

    if (isSameDate) {
      // Si es el mismo día, usar la hora
      chatDate = formatTime(date);
    } else if (isYesterday) {
      // Si es ayer
      chatDate = 'Ayer';
    } else {
      // Si no, usar la fecha formateada
      chatDate = formatDate(date);
    }

    return chatDate;
  }

  adjustTextareaHeight(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';  // Resetea la altura para obtener el scrollHeight correcto
    textarea.style.height = Math.min(textarea.scrollHeight, 6 * parseFloat(getComputedStyle(textarea).lineHeight)) + 'px';
  }

  adjustTimeToSystemTimezone(dateString: string): { iso: string, date: string, time: string } {
    const localDate = new Date(dateString);
    const timezoneOffset = localDate.getTimezoneOffset() * 60000; // Obtener el offset en milisegundos
    const adjustedTime = new Date(localDate.getTime() - timezoneOffset); // Ajustar a la hora del sistema
    const isoString = new Date(adjustedTime).toISOString(); // Ajustar la hora y obtener la cadena ISO correcta
    const datePart = adjustedTime.toLocaleDateString(); // Formato dd/mm/aaaa
    const timePart = adjustedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); // Formato hh:mm

    return {
      iso: isoString,
      date: datePart,
      time: timePart
    };
  }




}
